import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import AssignedUserFilter from './AssignedUserFilter';
import DateFilter from './DateFilter';
import EscrituraFilter from './EscrituraFilter';
import RegistroFilter from './RegistroFilter';
import EstadoFilter from './EstadoFilter';
import BloqueadaFilter from './BloqueadaFilter';
import ArchivadoFilter from './ArchivadoFilter';
import { useAuth } from '@/auth';
import { canArchivarEscrituras, checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';

interface FiltersModalProps {
  open: boolean;
  onClose: () => void;
  dateFrom: string;
  dateTo: string;
  dateField: string;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onDateFieldChange: (field: string) => void;
  onClearDateFilter: () => void;
  escrituraFilter: string;
  onEscrituraFilterChange: (filter: string) => void;
  onClearEscrituraFilter: () => void;
  registroFilter: string;
  onRegistroFilterChange: (filter: string) => void;
  onClearRegistroFilter: () => void;
  estadoFilter: string;
  onEstadoFilterChange: (filter: string) => void;
  onClearEstadoFilter: () => void;
  bloqueadaFilter: string;
  onBloqueadaFilterChange: (filter: string) => void;
  onClearBloqueadaFilter: () => void;
  archivadoFilter: string;
  onArchivadoFilterChange: (filter: string) => void;
  onClearArchivadoFilter: () => void;
  assignedUser: UserOption | null;
  onAssignedUserChange: (user: UserOption | null) => void;
  onClearAssignedUserFilter: () => void;
  onApplyFilters: () => void;
  onClearAllFilters?: () => void;
  /** El kanban ya separa las escrituras por estado, así que el filtro se oculta */
  showEscrituraFilter?: boolean;
}

const FiltersModal: React.FC<FiltersModalProps> = ({
  open,
  onClose,
  dateFrom,
  dateTo,
  dateField,
  onDateFromChange,
  onDateToChange,
  onDateFieldChange,
  onClearDateFilter,
  escrituraFilter,
  onEscrituraFilterChange,
  onClearEscrituraFilter,
  registroFilter,
  onRegistroFilterChange,
  onClearRegistroFilter,
  estadoFilter,
  onEstadoFilterChange,
  onClearEstadoFilter,
  bloqueadaFilter,
  onBloqueadaFilterChange,
  onClearBloqueadaFilter,
  archivadoFilter,
  onArchivadoFilterChange,
  onClearArchivadoFilter,
  assignedUser,
  onAssignedUserChange,
  onClearAssignedUserFilter,
  onApplyFilters,
  onClearAllFilters,
  showEscrituraFilter = true,
}) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];

  // Check permissions for tramite status visibility
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canViewTramiteStatus = checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_TRAMITE_STATUS.name]);
  const canSeeStatus = canManageTramites || canViewTramiteStatus;
  const canSeeEscritura = canSeeStatus && showEscrituraFilter; // Assuming the same permission logic applies for Escritura filter
  const canSeeBloqueo = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_BLOQUEO_TRAMITE.name]);
  const canSeeArchivado = canArchivarEscrituras(userPermissions);

  const handleClearAllFilters = () => {
    // If a custom clear all handler is provided, use it (for sessionStorage clearing)
    // Otherwise, fall back to the default behavior (clearing individual filters)
    if (onClearAllFilters) {
      onClearAllFilters();
    } else {
      onClearDateFilter();
      onClearEscrituraFilter();
      onClearRegistroFilter();
      if (canSeeStatus) {
        onClearEstadoFilter();
      }
      if (canSeeBloqueo) {
        onClearBloqueadaFilter();
      }
      if (canSeeArchivado) {
        onClearArchivadoFilter();
      }
      onClearAssignedUserFilter();
    }
  };

  const handleApplyAndClose = () => {
    onApplyFilters();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90dvh] flex flex-col border border-primary/10 bg-background/95 p-0 shadow-xl">
        <DialogHeader className="bg-primary/5 px-6 py-5 border-b border-primary/10 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold leading-tight text-primary">
            Filtros
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Ajuste los criterios para refinar la búsqueda de trámites sin perder de vista la información clave.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Date Filter */}
            <div className="sm:col-span-2">
              <DateFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                dateField={dateField}
                onDateFromChange={onDateFromChange}
                onDateToChange={onDateToChange}
                onDateFieldChange={onDateFieldChange}
                onClearFilter={onClearDateFilter}
                inModal={true}
              />
            </div>

            <AssignedUserFilter
              assignedUser={assignedUser}
              onAssignedUserChange={onAssignedUserChange}
              onClearFilter={onClearAssignedUserFilter}
            />

            {/* Escritura Filter */}
            {canSeeEscritura && (
              <EscrituraFilter
                escrituraFilter={escrituraFilter}
                onEscrituraFilterChange={onEscrituraFilterChange}
                onClearFilter={onClearEscrituraFilter}
              />
            )}

            {/* Registro Filter */}
            <RegistroFilter
              registroFilter={registroFilter}
              onRegistroFilterChange={onRegistroFilterChange}
              onClearFilter={onClearRegistroFilter}
            />

            {/* Estado Filter - Only show if user has permission to see tramite status */}
            {canSeeStatus && (
              <EstadoFilter
                estadoFilter={estadoFilter}
                onEstadoFilterChange={onEstadoFilterChange}
                onClearFilter={onClearEstadoFilter}
              />
            )}

            {/* Bloqueada Filter - Only show if user can manage bloqueo */}
            {canSeeBloqueo && (
              <BloqueadaFilter
                bloqueadaFilter={bloqueadaFilter}
                onBloqueadaFilterChange={onBloqueadaFilterChange}
                onClearFilter={onClearBloqueadaFilter}
              />
            )}

            {/* Archivado Filter - Only show if user can archive */}
            {canSeeArchivado && (
              <ArchivadoFilter
                archivadoFilter={archivadoFilter}
                onArchivadoFilterChange={onArchivadoFilterChange}
                onClearFilter={onClearArchivadoFilter}
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-3 border-t border-primary/10 bg-primary/5 px-6 py-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClearAllFilters}
            className="w-full sm:w-auto"
          >
            Limpiar todo
          </Button>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-0 flex-1 sm:flex-none"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApplyAndClose}
              className="min-w-0 flex-1 sm:flex-none"
            >
              Aplicar filtros
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FiltersModal;
